import type { PlayerResponse, RecStatusResponse } from '@on3/api';
import { On3IconArrow } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { useSite } from '@on3/ui-lib/index';
import { ConditionalLink } from '@public/components/UI/StarRating/StarRating';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { format } from 'date-fns';

import styles from './SlimTransferStatus.module.scss';
import { UndecidedStatus } from './UndecidedStatus';

interface ISlimTransferStatus {
  player?: PlayerResponse;
  status?: RecStatusResponse;
  className?: string;
}
export const SlimTransferStatus = ({
  status,
  player,
  className,
}: ISlimTransferStatus) => {
  const {
    type,
    date,
    committedAsset,
    transferredAsset,
    transferWithdrawn,
    recruitmentYear,
  } = status ?? {};
  const { defaultSport, classYear } = player || {};
  const { defaultYears, currentSite } = useSite();
  const { footballRecruitingYear, nonFootballRecruitingYear } = defaultYears;

  const isShortTermSignee = status?.shortTermSignee;
  const isShortTermTeam =
    status?.transferredAsset?.key === currentSite?.organizationKey;
  const showSortTermLabel = isShortTermSignee && !isShortTermTeam;

  const sport = defaultSport ? defaultSport?.name?.toLowerCase() : 'football';
  const recruitingYear =
    sport === 'football' ? footballRecruitingYear : nonFootballRecruitingYear;
  const commitDate = date && format(new Date(date), 'M/dd/yy');
  const transferWithdrawnDate =
    transferWithdrawn && format(new Date(transferWithdrawn), 'M/dd/yy');

  const hasStatus =
    type !== 'None' && type !== 'Withdrawn' && committedAsset?.slug;
  const withdrawn = type === 'Withdrawn';
  const convertedRecruitingYear = parseInt(recruitingYear.toString(), 10);

  const commitYear =
    classYear && convertedRecruitingYear >= classYear
      ? convertedRecruitingYear
      : classYear;
  const useLastTeamLink =
    !!transferredAsset?.slug && (!!recruitmentYear || !!commitYear);
  const useNewTeamLink =
    !!committedAsset?.slug && (!!recruitmentYear || !!commitYear);

  return (
    <div className={clsx(styles.transferContainer, className)}>
      {showSortTermLabel && (
        <Text className={styles.shortTermLabel} variant="subtitle1">
          Short-Term Signee
        </Text>
      )}
      <div className={styles.teamsContainer}>
        <ConditionalLink
          className={styles.lastTeam}
          condition={useLastTeamLink}
          href={`/college/${transferredAsset?.slug}/${sport}/${
            recruitmentYear ?? commitYear
          }/transfers/`}
        >
          <figure>
            <Avatar
              className={styles.teamLogo}
              domain={transferredAsset?.defaultAsset?.domain}
              height={20}
              source={transferredAsset?.defaultAsset?.source}
              teamAvatar
              width={20}
            />
          </figure>
        </ConditionalLink>
        <On3IconArrow />

        {hasStatus && (
          <ConditionalLink
            className={styles.newTeam}
            condition={useNewTeamLink}
            href={`/college/${committedAsset.slug}/${sport}/${
              recruitmentYear ?? commitYear
            }/transfers/`}
          >
            <figure>
              <Avatar
                className={styles.teamLogo}
                domain={committedAsset.defaultAsset?.domain}
                height={20}
                source={committedAsset.defaultAsset?.source}
                teamAvatar
                width={20}
              />
            </figure>
          </ConditionalLink>
        )}

        {!hasStatus && !withdrawn && (
          <UndecidedStatus className={styles.undecided} hideText />
        )}

        {withdrawn && (
          <figure className={styles.newTeam}>
            <Avatar
              className={styles.teamLogo}
              domain={transferredAsset?.defaultAsset?.domain}
              height={20}
              source={transferredAsset?.defaultAsset?.source}
              teamAvatar
              width={20}
            />
          </figure>
        )}
      </div>
      <div className={styles.commitDateContainer}>
        {showSortTermLabel && (
          <Text className={styles.shortTermLabelMobile} variant="subtitle1">
            Short-Term Signee
          </Text>
        )}
        {commitDate && <Text className={styles.commitDate}>{commitDate}</Text>}
        {transferWithdrawnDate && !commitDate && (
          <Text className={styles.commitDate}>{transferWithdrawnDate}</Text>
        )}
      </div>
    </div>
  );
};
